import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { fetchFeaturedVideos, streamUrl, BRAND_VIDEO_POSTER, trackVideoView } from "@/lib/api";
import { ViewsBadge } from "@/components/VideoCard";

export default function FeaturedVideo() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(0);
  const seen = useRef(new Set());

  useEffect(() => {
    fetchFeaturedVideos().then((v) => setVideos(v || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (videos.length < 2) return;
    const t = setInterval(() => setActive((i) => (i + 1) % videos.length), 10000);
    return () => clearInterval(t);
  }, [videos.length]);

  useEffect(() => {
    const cur = videos[active];
    if (!cur || seen.current.has(cur.id)) return;
    seen.current.add(cur.id);
    trackVideoView(cur.id).then((n) => {
      if (typeof n === "number") setVideos((prev) => prev.map((x) => (x.id === cur.id ? { ...x, views: n } : x)));
    });
  }, [active, videos]);

  if (videos.length === 0) return null;
  const v = videos[active];
  const entityPath = v.entity_type === "restaurant" ? `/directory/${v.entity_id}` : `/profile/${v.entity_id}`;

  return (
    <section data-testid="home-featured-video" className="bg-sand-900 text-sand-50 py-24 md:py-32 border-y border-sand-900">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-px bg-brand-500" />
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-ochre-400 flex items-center gap-2">
            <Play size={13} className="text-brand-500" /> Featured on Tastebuds TV
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-8">
            <div className="aspect-video bg-black border border-sand-700" data-testid="featured-video-player">
              {v.video_type === "upload" ? (
                <video
                  key={v.id}
                  src={streamUrl(v.id)}
                  poster={v.thumbnail_url || BRAND_VIDEO_POSTER}
                  controls
                  className="w-full h-full object-contain bg-black"
                  preload="metadata"
                />
              ) : (
                <iframe
                  key={v.id}
                  src={v.embed_url}
                  title={v.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            <h3 className="font-serif text-3xl md:text-4xl font-bold mt-6 leading-tight">{v.title}</h3>
            <div className="flex items-center gap-4 mt-2">
              {v.entity_name && (
                <Link to={entityPath} className="font-mono text-[11px] tracking-widest uppercase text-brand-300 hover:text-brand-500 transition-colors">
                  {v.entity_name} · {v.city}
                </Link>
              )}
              <ViewsBadge count={v.views} className="text-sand-400" />
            </div>
            {v.description && <p className="mt-3 text-sand-50/70 font-light max-w-2xl leading-relaxed">{v.description}</p>}
          </div>

          <div className="lg:col-span-4">
            <div className="flex items-center justify-between mb-5">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-400">Up Next</div>
              <Link to="/watch" data-testid="featured-video-see-all" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ochre-400 hover:text-brand-500 transition-colors">
                All Videos →
              </Link>
            </div>
            <div className="flex flex-col gap-3">
              {videos.map((item, i) => (
                <button
                  key={item.id}
                  data-testid={`featured-video-thumb-${item.id}`}
                  onClick={() => setActive(i)}
                  className={`group flex items-center gap-4 text-left border p-3 transition-colors ${
                    i === active ? "border-brand-500 bg-sand-800/60" : "border-sand-700 hover:border-brand-400"
                  }`}
                >
                  <div className="w-20 h-14 shrink-0 bg-black overflow-hidden">
                    <img
                      src={item.thumbnail_url || BRAND_VIDEO_POSTER}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="font-serif text-sm font-bold leading-snug truncate">{item.title}</div>
                    <div className="font-mono text-[9px] tracking-widest uppercase text-sand-400 mt-1 truncate">
                      {item.entity_name || item.city}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
